import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatError } from '@/lib/api';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_sales-support-1/artifacts/ofny598k_FD-Transparent.png';
const BG = 'https://images.unsplash.com/photo-1770321428577-4446e5cb7bab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJsdWUlMjBnZW9tZXRyaWMlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTU4MzQzNnww&ixlib=rb-4.1.0&q=85';

export default function LoginPage() {
  const [isReg, setIsReg] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  const [successMsg, setSuccessMsg] = useState('');

  if (user) { navigate('/dashboard', { replace: true }); return null; }

  const submit = async (e) => {
    e.preventDefault(); setError(''); setSuccessMsg(''); setBusy(true);
    try {
      if (isReg) {
        const res = await register(email, password, name, 'sales');
        if (res?.message?.includes('approval') || res?.pending_approval) {
          setSuccessMsg('Registration successful! Your account is pending admin approval. You will be able to log in once approved.');
          setIsReg(false);
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        await login(email, password);
        navigate('/dashboard', { replace: true });
      }
    } catch (e) {
      setError(formatError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + '/dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (err) {
      setError('Failed to initiate Google login. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8 animate-fade-in-up">
          <div>
            <img src={LOGO} alt="Fyredune" className="h-12 mb-6" />
            <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">{isReg ? 'Create Account' : 'Welcome Back'}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{isReg ? 'Get started with Fyredune CRM' : 'Sign in to your Fyredune CRM account'}</p>
          </div>
          {error && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="auth-error">{error}</div>}
          {successMsg && <div className="rounded-md border border-emerald-500/50 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-400" data-testid="auth-success">{successMsg}</div>}
          <form onSubmit={submit} className="space-y-4">
            {isReg && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <Input id="name" data-testid="name-input" placeholder="John Doe" value={name} onChange={e => setName(e.target.value)} className="pl-10" required />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input id="email" type="email" data-testid="email-input" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className="pl-10" required />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input id="password" type="password" data-testid="password-input" placeholder="Enter password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10" required />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="auth-submit-btn">
              {busy ? 'Please wait...' : (isReg ? 'Create Account' : 'Sign In')} <ArrowRight size={16} className="ml-2" />
            </Button>
          </form>
          <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">Or</span></div></div>
          <Button variant="outline" className="w-full" onClick={googleLogin} data-testid="google-login-btn">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            {isReg ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button onClick={() => { setIsReg(!isReg); setError(''); }} className="font-medium text-primary hover:underline" data-testid="toggle-auth-mode">{isReg ? 'Sign In' : 'Create Account'}</button>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="relative flex items-center justify-center p-12 text-white">
          <div className="max-w-lg text-center">
            <h2 className="text-4xl font-bold mb-4 font-heading">Manage Your Business, Simplified</h2>
            <p className="text-lg opacity-90">Sales pipeline, helpdesk, inventory — all in one powerful platform.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
